import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';

import './DockPanel.css';

const MOBILE_QUERY = '(max-width: 768px)';
// Enough of the header must stay on screen to drag the panel back.
const MIN_VISIBLE = 140;
// Panels stack in the order they were last touched. The base matches the
// stylesheet, which sits above the grid and navbar but below Bootstrap's own
// dialogs so a real modal still comes out in front.
const BASE_Z = 1035;
let topZ = BASE_Z;

/**
 * A modeless window that floats over the app rather than covering it.
 *
 * The wizard and the statistics panel were both full-screen dialogs, which put
 * them in front of the very thing they act on: the wizard previews art that the
 * grid behind it is already rendering, and picking a trait in the statistics
 * charts filters that same grid. A dialog that has to be dismissed to see its
 * own effect makes the artist work in round trips.
 *
 * On a desktop it is deliberately not a react-bootstrap Modal: a Modal traps
 * focus and blocks the page behind it, which is exactly the behaviour being
 * avoided.
 *
 * On a phone it is a Modal, because the reasoning above does not survive the
 * smaller screen. There is nowhere to move a panel to when it already fills the
 * display, so floating buys nothing and costs the dialog conventions people
 * expect — the back gesture, the backdrop, the screen reader treatment.
 */
class DockPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      x: null,
      y: null,
      width: props.defaultWidth || 900,
      height: props.defaultHeight || 620,
      collapsed: false,
      isMobile: false,
      z: ++topZ,
    };
    this.panel = React.createRef();
    this.drag = null;
  }

  componentDidMount() {
    this.media = window.matchMedia(MOBILE_QUERY);
    this.onMediaChange = (e) => this.setState({ isMobile: e.matches });
    // Safari before 14 only has the deprecated listener API.
    if (this.media.addEventListener) this.media.addEventListener('change', this.onMediaChange);
    else this.media.addListener(this.onMediaChange);

    window.addEventListener('resize', this.clampToViewport);
    window.addEventListener('keydown', this.onKeyDown);

    this.setState({ isMobile: this.media.matches }, this.restoreGeometry);
  }

  componentWillUnmount() {
    if (this.media) {
      if (this.media.removeEventListener) this.media.removeEventListener('change', this.onMediaChange);
      else this.media.removeListener(this.onMediaChange);
    }
    window.removeEventListener('resize', this.clampToViewport);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  get storageName() {
    return `dockPanel:${this.props.storageKey || this.props.title || 'panel'}`;
  }

  /**
   * Restore where this panel was last left. Storage can be unavailable or hold
   * something unusable, and neither is worth failing over — the panel simply
   * opens at its default place.
   */
  restoreGeometry = () => {
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(this.storageName));
    } catch (e) { /* opening at the default position is a fine outcome */ }

    if (saved && typeof saved === 'object') {
      this.setState(
        {
          x: typeof saved.x === 'number' ? saved.x : null,
          y: typeof saved.y === 'number' ? saved.y : null,
          width: typeof saved.width === 'number' ? saved.width : this.state.width,
          height: typeof saved.height === 'number' ? saved.height : this.state.height,
          collapsed: !!saved.collapsed,
        },
        this.clampToViewport
      );
      return;
    }
    this.clampToViewport();
  }

  persistGeometry = () => {
    if (this.state.isMobile) return; // Phone geometry is fixed; only remember the collapse.
    try {
      const { x, y, width, height, collapsed } = this.state;
      window.localStorage.setItem(this.storageName, JSON.stringify({ x, y, width, height, collapsed }));
    } catch (e) { /* not worth interrupting anything over */ }
  }

  /**
   * Keep the panel reachable. A window that was sized on a large display, or
   * left near an edge, must not open off-screen where it cannot be dragged back.
   */
  clampToViewport = () => {
    if (this.state.isMobile) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(this.state.width, Math.max(320, vw - 24));
    const height = Math.min(this.state.height, Math.max(200, vh - 24));
    const x = this.state.x === null ? Math.max(12, Math.round((vw - width) / 2)) : this.state.x;
    const y = this.state.y === null ? Math.max(12, Math.round((vh - height) / 2)) : this.state.y;

    this.setState({
      width,
      height,
      x: Math.min(Math.max(x, MIN_VISIBLE - width), vw - MIN_VISIBLE),
      y: Math.min(Math.max(y, 0), vh - 44),
    });
  }

  /**
   * Touching a panel raises it. With several open at once the one being worked
   * in has to be the one on top, and nothing else decides that for us.
   */
  bringToFront = () => {
    if (this.state.z === topZ) return;
    this.setState({ z: ++topZ });
  }

  onKeyDown = (e) => {
    if (e.key !== 'Escape' || !this.props.show) return;
    // Only when nothing inside is being typed into.
    const inside = this.panel.current && this.panel.current.contains(document.activeElement);
    const typing = inside && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName);
    if (!typing && this.props.onClose) this.props.onClose();
  }

  startDrag = (e) => {
    this.bringToFront();
    if (this.state.isMobile || e.button === 2) return;
    if (e.target.closest('button')) return; // the header's own controls
    this.drag = { mode: 'move', px: e.clientX, py: e.clientY, x: this.state.x, y: this.state.y };
    this.beginPointer(e);
  }

  startResize = (e) => {
    if (this.state.isMobile) return;
    this.drag = { mode: 'size', px: e.clientX, py: e.clientY, width: this.state.width, height: this.state.height };
    this.beginPointer(e);
  }

  beginPointer = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    this.pointerTarget = e.currentTarget;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.endPointer);
  }

  onPointerMove = (e) => {
    if (!this.drag) return;
    const dx = e.clientX - this.drag.px;
    const dy = e.clientY - this.drag.py;

    if (this.drag.mode === 'move') {
      const width = this.state.width;
      this.setState({
        x: Math.min(Math.max(this.drag.x + dx, MIN_VISIBLE - width), window.innerWidth - MIN_VISIBLE),
        y: Math.min(Math.max(this.drag.y + dy, 0), window.innerHeight - 44),
      });
    } else {
      this.setState({
        width: Math.max(this.props.minWidth || 360, Math.min(this.drag.width + dx, window.innerWidth - this.state.x - 8)),
        height: Math.max(this.props.minHeight || 220, Math.min(this.drag.height + dy, window.innerHeight - this.state.y - 8)),
      });
    }
  }

  endPointer = () => {
    this.drag = null;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.endPointer);
    this.persistGeometry();
  }

  /**
   * Double-clicking the bar rolls the panel up, the way a title bar does
   * everywhere else. Ignored on the buttons, which have their own meaning.
   */
  onBarDoubleClick = (e) => {
    if (e.target.closest('button')) return;
    this.toggleCollapsed();
  }

  toggleCollapsed = () => {
    this.setState((s) => ({ collapsed: !s.collapsed }), this.persistGeometry);
  }

  render() {
    const { show, title, onClose, children, footer, unstyled } = this.props;
    const { x, y, width, height, collapsed, isMobile } = this.state;
    if (!show) return null;

    // A phone has no room to float anything, and nothing useful is revealed by
    // moving a panel aside on a screen the panel already fills. So there it
    // stays an ordinary dialog, which is also what the platform's own gestures
    // and screen readers expect.
    if (isMobile) {
      return (
        <Modal show={show} onHide={onClose} centered className={this.props.unstyled ? 'dock-plain' : undefined}>
          <Modal.Header closeButton>
            <Modal.Title className="font-display">{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 0, height: '72vh', overflowY: 'auto' }}>
            {children}
          </Modal.Body>
          {footer ? <Modal.Footer>{footer}</Modal.Footer> : null}
        </Modal>
      );
    }

    const panel = (
      <section
        ref={this.panel}
        className={`dock-panel${collapsed ? ' dock-panel--collapsed' : ''}`}
        style={{ left: `${x || 0}px`, top: `${y || 0}px`, width: `${width}px`, zIndex: this.state.z }}
        onPointerDownCapture={this.bringToFront}
        role="dialog"
        aria-modal="false"
        aria-label={typeof title === 'string' ? title : (this.props.storageKey || 'panel')}
      >
        <header className="dock-panel__bar" onPointerDown={this.startDrag} onDoubleClick={this.onBarDoubleClick}>
          <span className="dock-panel__title font-display">{title}</span>
          <span className="dock-panel__actions">
            <button
              type="button"
              className="dock-panel__button"
              onClick={this.toggleCollapsed}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? '▲' : '▼'}
            </button>
            <button
              type="button"
              className="dock-panel__button"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              ✕
            </button>
          </span>
        </header>

        {!collapsed && (
          <div className="dock-panel__body" style={{ height: `${height}px` }}>
            {children}
          </div>
        )}

        {!collapsed && footer ? <footer className="dock-panel__footer">{footer}</footer> : null}

        {!collapsed ? (
          <div
            className="dock-panel__grip"
            onPointerDown={this.startResize}
            role="separator"
            aria-label="Resize"
          />
        ) : null}
      </section>
    );

    // A project's stylesheet is scoped to #root, so a panel that should stay
    // neutral is rendered outside it. Trading, settings and platform screens
    // are the platform speaking, not the artwork.
    return unstyled ? ReactDOM.createPortal(panel, document.body) : panel;
  }
}

export default DockPanel;
